import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
// Direct, not via @workspace/ui/lib/icons: `optimizePackageImports` matches on
// the import specifier and can't see through that re-export. Build-speed only —
// it does NOT shrink the bundle while gen-ui's namespace import retains the full
// icon set (tdd-0074).
import { ArrowUp } from "lucide-react";
import { cn } from "../../utils";

/**
 * Decorative pseudo-placeholder that cycles through example prompts.
 *
 * WHY THIS EXISTS RATHER THAN A `placeholder` ATTRIBUTE
 *
 * A native `placeholder` is a single string on the textarea — it cannot be
 * split into elements, so it cannot be animated or transitioned between values.
 * This renders the same visual affordance as real DOM.
 *
 * The real `placeholder` is still on the textarea underneath and is what
 * assistive tech announces; this layer is `aria-hidden` so screen readers hear
 * one label, not two. Composer hides the native one visually when an overlay is
 * present.
 *
 * WHY CSS RATHER THAN framer-motion
 *
 * The reference implementation used framer-motion's AnimatePresence. That is a
 * ~50KB runtime and would have been this repo's first animation library — for a
 * two-state enter/exit on a decorative label. The same result (translate + fade,
 * exit up / enter from below) is a pair of CSS transitions, so the effect is
 * ported rather than the dependency.
 *
 * The trade: no layout/gesture animation and no interruption-safe tweening. If
 * motion work spreads across apps, revisit and hoist a real primitive into
 * @workspace/ui rather than growing this.
 *
 * HOW THE CYCLE WORKS
 *
 * Both the outgoing and incoming line are mounted during a swap — the outgoing
 * one is what a CSS-only approach otherwise cannot animate, since removing it
 * from the DOM skips its exit transition. `phase` drives which class each gets:
 *
 *   idle     — current line resting at y=0, opacity 1
 *   leaving  — current line translates up and fades; next line starts below
 *   (swap)   — next becomes current, phase returns to idle
 */

/** How long each suggestion rests before swapping. */
const DEFAULT_INTERVAL_MS = 3200;
/** Must match the duration in the transition classes below. */
const TRANSITION_MS = 320;

export interface PromptSuggestionProps {
  /**
   * Suggestions to cycle through. One entry renders statically with no
   * animation; an empty list renders nothing.
   */
  texts: string[];
  /**
   * Hide the overlay. Callers pass `true` once the user types, since a fake
   * placeholder behind real text reads as a rendering bug. Cycling pauses while
   * hidden — animating something invisible burns frames for nothing.
   */
  hidden?: boolean;
  /** Milliseconds each suggestion is shown. */
  intervalMs?: number;
  /** Extra classes on the positioning layer (alignment/padding per state). */
  className?: string;
  /** Extra classes on the animated line. */
  lineClassName?: string;
  /**
   * Makes the suggestion actionable: renders a send button and calls this with
   * the suggestion **currently on screen**.
   *
   * The text is passed as an argument rather than read by the parent because
   * the cycling index lives in here — a parent tracking it separately would
   * submit whatever it last heard about, which during a swap is the previous
   * suggestion, not the visible one.
   */
  onSubmit?: (text: string) => void;
}

/**
 * Keyboard control surface, for a parent that wants ↑/↓ to move between
 * suggestions and Enter to send the visible one.
 *
 * Exposed imperatively rather than by lifting `index` out: the cycling timer,
 * the enter/leave phases and the swap all depend on that state being owned
 * here, and hoisting it would put the animation's internals in the caller's
 * hands for the sake of two commands.
 */
export interface PromptSuggestionHandle {
  /** Step to the next/previous suggestion, wrapping at both ends. */
  step: (delta: 1 | -1) => void;
  /** The suggestion currently on screen. */
  current: () => string;
}

export const PromptSuggestion = forwardRef<
  PromptSuggestionHandle,
  PromptSuggestionProps
>(function PromptSuggestion(
  {
    texts,
    hidden = false,
    intervalMs = DEFAULT_INTERVAL_MS,
    className,
    lineClassName,
    onSubmit,
  },
  handleRef,
) {
  const [index, setIndex] = useState(0);
  const [leaving, setLeaving] = useState(false);
  /**
   * True for one frame after a swap, so the incoming line paints in its
   * below-and-invisible position before transitioning to rest.
   *
   * Needed because `key={index}` remounts the element: a freshly mounted node
   * has no previous computed style, so applying the resting style immediately
   * gives the browser nothing to transition *from* and the text simply pops in.
   * Verified — before this, the outgoing line animated but the incoming one
   * jumped straight to translateY(0)/opacity 1.
   */
  const [entering, setEntering] = useState(false);
  // Held in a ref so the effect below doesn't re-run (and restart the timer)
  // every time the parent re-renders with a new array literal.
  const countRef = useRef(texts.length);
  countRef.current = texts.length;

  /**
   * Stop auto-cycling once the user drives with the arrows.
   *
   * Without this the carousel keeps ticking underneath them: they press ↓ to
   * reach a suggestion, and ~3s later it drifts to the next one on its own —
   * so Enter sends something they did not choose. Manual control is a clear
   * signal they are reading rather than watching, and it never resumes for
   * the life of the overlay.
   */
  const [manual, setManual] = useState(false);
  /**
   * Which way the swap travels. Forward (auto-cycle, ↓) sends the outgoing
   * line up and brings the next in from below; ↑ mirrors it, so the motion
   * follows the key rather than contradicting it.
   */
  const [direction, setDirection] = useState<1 | -1>(1);
  const shouldCycle = texts.length > 1 && !hidden && !manual;

  useEffect(() => {
    if (!shouldCycle) return;

    let swapTimer: ReturnType<typeof setTimeout>;

    const tick = setInterval(() => {
      setDirection(1);
      setLeaving(true);
      // Let the exit transition play, then swap the text and reset. Resetting
      // `leaving` in the same tick as the index change means the incoming line
      // mounts already in its "below" position and transitions up from there.
      swapTimer = setTimeout(() => {
        setIndex((i) => (i + 1) % countRef.current);
        setLeaving(false);
        setEntering(true);
      }, TRANSITION_MS);
    }, intervalMs);

    return () => {
      clearInterval(tick);
      clearTimeout(swapTimer);
    };
  }, [shouldCycle, intervalMs]);

  // Release the entering state once the browser has painted the "below"
  // position, so the transition to rest actually has a starting point.
  // Double rAF: the first fires before the paint that applies the entering
  // style, the second after it.
  useEffect(() => {
    if (!entering) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setEntering(false));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [entering, index]);

  // Coming back from hidden (user cleared the input) should resume from a
  // settled state rather than mid-transition.
  useEffect(() => {
    if (hidden) {
      setLeaving(false);
      setEntering(false);
      // Hiding means the user started typing or sent something; when the
      // overlay next appears it should behave like a fresh one and cycle again.
      setManual(false);
    }
  }, [hidden]);

  // Kept in a ref so the imperative handle can read the live value without
  // being rebuilt on every index change.
  const textsRef = useRef(texts);
  textsRef.current = texts;
  const indexRef = useRef(index);
  indexRef.current = index;
  // The imperative handle is built once, so it cannot read `leaving`/`entering`
  // from its closure — those would be captured at their mount values.
  const leavingRef = useRef(leaving);
  leavingRef.current = leaving;
  const enteringRef = useRef(entering);
  enteringRef.current = entering;
  // Manual swaps schedule their own timer, outside the cycle effect's cleanup.
  // Tracked so unmounting mid-swap cannot set state on a dead component.
  const stepTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(stepTimerRef.current), []);

  useImperativeHandle(
    handleRef,
    () => ({
      step: (delta) => {
        const n = textsRef.current.length;
        if (n === 0) return;
        // Ignore a press that lands mid-swap. Restarting the sequence from a
        // half-played phase strands the line at whatever transform it had
        // reached, and holding an arrow down would otherwise queue swaps
        // faster than they can play.
        if (leavingRef.current || enteringRef.current) return;
        setManual(true);
        setDirection(delta);
        // Same three phases as the auto-cycle, so an arrow press and a natural
        // swap are indistinguishable — only the direction differs.
        setLeaving(true);
        stepTimerRef.current = setTimeout(() => {
          setIndex((i) => (i + delta + n) % n);
          setLeaving(false);
          setEntering(true);
        }, TRANSITION_MS);
      },
      current: () =>
        textsRef.current[
          Math.min(indexRef.current, textsRef.current.length - 1)
        ] ?? "",
    }),
    [],
  );

  if (texts.length === 0) return null;

  const text = texts[Math.min(index, texts.length - 1)];

  return (
    <div
      className={cn(
        // Matches the Textarea's own padding so the overlay sits exactly where
        // the native placeholder would. If the textarea's padding changes, this
        // needs to change with it.
        "absolute inset-0 flex items-start gap-2 p-3",
        // Inherited from Composer's wrapper, but repeated so the component is
        // safe to use outside `overlaySlot` too. The send button opts back in
        // individually — see below.
        "pointer-events-none select-none",
        "transition-opacity duration-200",
        hidden ? "opacity-0" : "opacity-100",
        className,
      )}
    >
      {/* Clip layer — keeps a translating line from escaping the input box.
          `aria-hidden` is scoped to the TEXT, not the whole overlay: the
          textarea's real placeholder is the accessible label, so announcing this
          too would double up — but hiding the send button from assistive tech
          while leaving it tabbable would be a worse bug. */}
      <div
        aria-hidden="true"
        className="relative min-w-0 flex-1 overflow-hidden"
      >
        {/* Line layer — owns the transform/opacity transition.
            `key={index}` remounts per suggestion. That matters: a remounted
            element starts from its *entering* classes and transitions to the
            resting ones, which is what produces the rise-from-below. Without
            the remount the incoming text would inherit the outgoing line's
            settled transform and simply snap. */}
        <div
          key={index}
          // Transform and opacity are inline rather than utility classes.
          // Tailwind only generates utilities it finds by scanning source, and
          // the translate variants used here did not survive that — the class
          // landed on the element but computed to `transform: none`, so the text
          // faded without ever moving. That failure is silent, so the values are
          // stated directly where they cannot be tree-shaken away.
          style={{
            // leaving  → travel up and fade out
            // entering → start below and invisible (one frame only)
            // rest     → settled
            // `direction` flips both halves together: going backwards, the
            // outgoing line drops and the incoming one arrives from above.
            transform: leaving
              ? `translateY(${direction === 1 ? "-1.25rem" : "1.25rem"})`
              : entering
                ? `translateY(${direction === 1 ? "1.25rem" : "-1.25rem"})`
                : "translateY(0)",
            opacity: leaving || entering ? 0 : 1,
            // No transition on the entering frame: that frame is the *setup* of
            // the start position, and animating into it would show the text
            // sliding down before it rises.
            transitionProperty: entering ? "none" : "transform, opacity",
            transitionDuration: `${TRANSITION_MS}ms`,
            transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
          }}
          className={cn(
            "text-md text-muted-foreground",
            // Respect a user's reduced-motion preference: the text still cycles
            // (the information is the point), it just swaps without travel.
            "motion-reduce:transition-none",
            lineClassName,
          )}
        >
          {/* Text layer — its own node so an effect can target the characters
              without disturbing the line's transform. */}
          <span className="block whitespace-nowrap">{text}</span>
        </div>
      </div>

      {/* Send the visible suggestion. `pointer-events-auto` re-enables clicks
          that the wrapper turns off — without it this is inert.
          `text` is read at click time, so a swap mid-click sends what the user
          actually saw. Hidden while the overlay is fading out so it can't be
          clicked after the user has started typing. */}
      {onSubmit && !hidden && (
        <button
          type="button"
          onClick={() => onSubmit(text)}
          aria-label={`Send suggestion: ${text}`}
          className={cn(
            "pointer-events-auto relative flex-shrink-0 self-center right-[-1rem]",
            "inline-flex items-center justify-center h-14 w-14 rounded-full",
            "text-muted-foreground hover:text-foreground hover:bg-muted",
            "transition-colors",
          )}
        >
          <ArrowUp className="h-6 w-6" />
        </button>
      )}
    </div>
  );
});
